import React from "react";
import { Tag, Tooltip } from "antd";
import type { JsonbPair } from "./sections";

export type MoneyKind = "in" | "out" | "commission" | "cleaning" | "neutral";

// MUI Chip reemplazado por AntD Tag manteniendo los colores semánticos
// exactos (ingreso verde, egreso rojo, limpieza celeste, comisión azul).
export const MoneyChip: React.FC<{
  value: number;
  kind?: MoneyKind;
  bold?: boolean;
  currency?: "CLP" | "USD";
}> = ({ value, kind = "neutral", bold, currency = "CLP" }) => {
  const n = Number(value) || 0;
  const isZero = n === 0;

  const style: React.CSSProperties = {
    fontWeight: bold ? 700 : 500,
    fontVariantNumeric: "tabular-nums",
    minWidth: 90,
    textAlign: "center",
  };

  let tagColor: string | undefined;
  if (!isZero) {
    switch (kind) {
      case "in":
        style.backgroundColor = "#C8E6C9";
        style.color = "#1B5E20";
        style.borderColor = "#A5D6A7";
        break;
      case "out":
        style.backgroundColor = "#FFCDD2";
        style.color = "#B71C1C";
        style.borderColor = "#EF9A9A";
        break;
      case "commission":
        tagColor = "blue";
        break;
      case "cleaning":
        style.backgroundColor = "#e0f7fa";
        style.color = "#006064";
        style.borderColor = "#80DEEA";
        break;
      case "neutral":
        tagColor = "blue";
        break;
    }
  }

  const maximumFractionDigits = currency === "USD" ? 2 : 0;

  return (
    <Tag color={tagColor} style={style} bordered={isZero}>
      {new Intl.NumberFormat("es-CL", {
        style: "currency",
        currency,
        maximumFractionDigits,
      }).format(n)}
    </Tag>
  );
};

export const money = (v: number, c = "CLP") =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: c,
    maximumFractionDigits: 0,
  }).format(v || 0);

export const catColor: Record<string, string> = {
  aseo: "blue",
  lavanderia: "geekblue",
  reparacion: "volcano",
  supervision: "purple",
  compra: "green",
  transporte: "purple",
  computacion: "lime",
  fotografia: "orange",
  publicidad: "gold",
  cuenta: "geekblue",
  emergencia: "red",
  sueldo: "magenta",
  reembolso: "cyan",
  otros: "default",
};

export const ListCol: React.FC<{
  items?: JsonbPair[] | null;
  field: "razon" | "monto";
  kind?: "in" | "out" | "neutral";
  currency?: "CLP" | "USD";
}> = ({ items, field, kind = "neutral", currency = "CLP" }) => {
  const safeItems = Array.isArray(items) ? items : items ? [items] : [];
  if (safeItems.length === 0) {
    return <div style={{ opacity: 0.6 }}>—</div>;
  }
  const toSafeText = (v: any) =>
    v === null || v === undefined
      ? "—"
      : typeof v === "object"
        ? JSON.stringify(v)
        : String(v);

  return (
    <div style={{ display: "grid", rowGap: 4 }}>
      {safeItems.map((it, i) =>
        field === "razon" ? (
          <div
            key={i}
            title={toSafeText(it?.razon)}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {toSafeText(it?.razon)}
          </div>
        ) : (
          <div key={i} style={{ textAlign: "right" }}>
            <MoneyChip
              value={Number(it?.monto) || 0}
              kind={kind}
              currency={currency}
            />
          </div>
        ),
      )}
    </div>
  );
};

export const buildJsonbGroup = (opts: {
  key: string;
  title: React.ReactNode;
  listKey: string;
  kind: "in" | "out";
}) => {
  const { key, title, listKey, kind } = opts;

  return {
    key,
    title,
    children: [
      {
        key: `${listKey}_razon`,
        title: "Razón",
        width: 150,
        align: "center" as const,
        render: (_: any, r: any) => (
          <ListCol items={r?.[listKey]} field="razon" kind={kind} />
        ),
      },
      {
        key: `${listKey}_monto`,
        title: "Monto",
        width: 120,
        align: "center" as const,
        render: (_: any, r: any) => (
          <ListCol items={r?.[listKey]} field="monto" kind={kind} />
        ),
      },
    ],
  };
};

export const pretty = (k: string) =>
  k.replace(/_/g, " ").replace(/\b\w/g, (t) => t.toUpperCase());

export const DATE_KEYS = new Set(["fechaCreacion", "ingreso", "salida"]);

export const CLP_KEYS = new Set([
  "reservaTotalClp",
  "precioNochesClp",
  "limpiezaClp",
  "comisionPlataformaClp",
  "impuestoPlataformaClp",
  "comisionAfNochesClp",
  "comisionAfExtrasClp",
  "ivaComisionDeNochesClp",
  "ivaComisionDeExtrasClp",
  "totalExtrasClp",
  "totalPropietario",
]);

export const USD_KEYS = new Set([
  "reservaTotalUsd",
  "precioNochesUsd",
  "limpiezaUsd",
  "comisionPlataformaUsd",
  "impuestoPlataformaUsd",
  "comisionAfNochesUsd",
  "comisionAfExtrasUsd",
  "ivaComisionDeNochesUsd",
  "ivaComisionDeExtrasUsd",
  "totalExtrasUsd",
]);

export const ADMIN_DETAILED_KEYS = [
  "id",
  "ingreso",
  "salida",
  "plataforma",
  "nombreHuesped",
  "telefonoHuesped",
  "nombrePropiedad",
  "reservaTotalClp",
  "idPropiedadL",
  "idReservaV",
  "idReservaC",
  "idReservaPlataforma",
  "huespedes",
  "noches",
  "idPlataforma",
  "moneda",
  "cambioUsdClp",
  "reservaTotalUsd",
  "precioNochesClp",
  "precioNochesUsd",
  "limpiezaClp",
  "limpiezaUsd",
  "comisionPlataformaClp",
  "comisionPlataformaUsd",
  "extrasComisionClp",
  "extrasComisionUsd",
  "extrasSinComisionClp",
  "extrasSinComisionUsd",
  "impuestoPlataformaClp",
  "impuestoPlataformaUsd",
  "descuentosClp",
  "descuentosUsd",
  "tasaComisionAf",
  "comisionAfNochesClp",
  "comisionAfExtrasClp",
  "comisionAfNochesUsd",
  "comisionAfExtrasUsd",
  "ivaComisionDeNochesClp",
  "ivaComisionDeExtrasClp",
  "ivaComisionDeNochesUsd",
  "totalExtrasClp",
  "totalExtrasUsd",
  "ivaComisionDeExtrasUsd",
  "totalPropietario",
  "estadoPago",
  "tipoPago",
  "action",
  "tipo",
  "inmutable",
  "notaInterna",
  "fechaCreacion",
];

export const ADMIN_SUMMARY_KEYS = [
  "id",
  "fechaCreacion",
  "nombrePropiedad",
  "idReservaC",
  "idReservaPlataforma",
  "nombreHuesped",
  "telefonoHuesped",
  "ingreso",
  "salida",
  "plataforma",
  "reservaTotalClp",
  "precioNochesClp",
  "limpiezaClp",
  "comisionPlataformaClp",
  "extrasComisionClp",
  "extrasSinComisionClp",
  "impuestoPlataformaClp",
  "descuentosClp",
  "comisionAfNochesClp",
  "comisionAfExtrasClp",
  "ivaComisionDeNochesClp",
  "ivaComisionDeExtrasClp",
];

export const isJsonbListKey = (k: string) =>
  k === "extrasComisionClp" ||
  k === "extrasComisionUsd" ||
  k === "extrasSinComisionClp" ||
  k === "extrasSinComisionUsd" ||
  k === "descuentosClp" ||
  k === "descuentosUsd" ||
  k === "impuestoPlataformaClp" ||
  k === "impuestoPlataformaUsd";

export const moneyKindFor = (key: string): MoneyKind => {
  if (/^precioNoches/.test(key)) return "in";
  if (/^limpieza/.test(key)) return "out";
  if (/^comisionPlataforma/.test(key)) return "out";
  if (
    /^comisionAfNoches/.test(key) ||
    /^comisionAfExtras/.test(key) ||
    /^ivaComision/.test(key)
  )
    return "out";
  if (/^impuestoPlataforma/.test(key)) return "out";
  if (/^totalExtras/.test(key) || /^reservaTotal/) return "in";
  if (key === "totalPropietario") return "in";
  return "neutral";
};

export const isOwnerTotal = (key: string) => key === "totalPropietario";

export const stackTitle = (s: string) => {
  const words = s.split(/\s+/).filter(Boolean);
  return (
    <span className="stack-title">
      {words.map((w, i) => (
        <span key={i} className="stack-title-word">
          {w}
        </span>
      ))}
    </span>
  );
};

export const CompactTitle: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title} placement="top">
    {stackTitle(title)}
  </Tooltip>
);
